/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : "Where do you want to save this template?" — explicit, never overwrites.
 */

import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { Tenant, Trade } from '../types';

export interface SaveTargets { toProvider: boolean; toCurrentTenant: boolean; tenantIds: string[]; note: string; }

export interface SaveTargetModalProps {
  open: boolean; trade: Trade | null; tenants: Tenant[]; currentTenantId: string | null;
  /** Pre-check "Provider default" (e.g. when establishing a trade's first default from the Global Default). */
  recommendProvider?: boolean;
  onClose: () => void; onConfirm: (targets: SaveTargets) => void;
}

/** Sentinel value for the "All tenants" toggle row in the tenant multi-select (never a real tenantId). */
const ALL_TENANTS = '__all_tenants__';

/** "Where do you want to save this template?" — checkbox-based, explicit, never overwrites, always creates a new version. */
export default function SaveTargetModal({ open, trade, tenants, currentTenantId, recommendProvider, onClose, onConfirm }: SaveTargetModalProps) {
  // A tenant template can only be saved to itself or promoted to the provider default — templates are
  // never shared directly between tenants (no "other tenants" picker there). The one exception is
  // pushing the PROVIDER DEFAULT for a specific trade out to tenants already adopting that trade —
  // that's a provider->many-tenants publish (the same direction saveTradeTemplate always supported via
  // tenantIds), not tenant-to-tenant sharing. It only applies while editing a trade's Provider default
  // (no current tenant, and a specific trade — never the Global Default, which has no trade/adopters).
  const hasCurrentTenant = !!currentTenantId;
  const mappedTenantIds = trade ? Object.keys(trade.tenants) : [];
  const mappedTenants = mappedTenantIds.map(id => tenants.find(t => t.id === id) ?? { id, name: id });
  const canPushAllTenants = !hasCurrentTenant && !!trade && mappedTenantIds.length > 0;
  const [toProvider, setToProvider] = useState(!!recommendProvider || !hasCurrentTenant);
  const [toCurrentTenant, setToCurrentTenant] = useState(hasCurrentTenant);
  // Specific tenants to push to — opt-in, never pre-checked. May be any subset of mappedTenantIds, or
  // all of them (via the "All tenants" row in the same dropdown, exactly like the NAOMI mapping picker).
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const allSelected = mappedTenantIds.length > 0 && tenantIds.length === mappedTenantIds.length;
  const anyTarget = toProvider || (toCurrentTenant && hasCurrentTenant) || (canPushAllTenants && tenantIds.length > 0);

  const handleTenantChange = (e: SelectChangeEvent<string[]>) => {
    const value = e.target.value;
    const picked = typeof value === 'string' ? value.split(',') : value;
    if (picked.includes(ALL_TENANTS)) {
      setTenantIds(allSelected ? [] : mappedTenantIds); // toggle: select all, or clear if already all
    } else {
      setTenantIds(picked);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Where do you want to save this template?</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Each destination gets its own new version. Nothing is overwritten.</Typography>

        <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: toProvider ? (theme => alpha(theme.palette.error.main, 0.08)) : 'grey.50', mb: 1 }}>
          <FormControlLabel control={<Checkbox checked={toProvider} onChange={e => setToProvider(e.target.checked)} />}
            label={<Box><Typography fontWeight={600} fontSize={13.5}>Provider default (for {trade?.name})</Typography>
              <Typography variant="caption" color="error">Separate, sensitive action — affects the base every new tenant starts from.</Typography></Box>} />
        </Box>

        {hasCurrentTenant && (
          <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: toCurrentTenant ? (theme => alpha(theme.palette.primary.main, 0.08)) : 'grey.50', mb: 1 }}>
            <FormControlLabel control={<Checkbox checked={toCurrentTenant} onChange={e => setToCurrentTenant(e.target.checked)} />}
              label={<Box><Typography fontWeight={600} fontSize={13.5}>Current tenant — {tenants.find(t => t.id === currentTenantId)?.name ?? currentTenantId}</Typography>
                <Typography variant="caption" color="text.secondary">New version in this tenant's own history.</Typography></Box>} />
          </Box>
        )}

        {canPushAllTenants && (
          <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: tenantIds.length ? (theme => alpha(theme.palette.warning.main, 0.12)) : 'grey.50', mb: 1 }}>
            <Typography fontWeight={600} fontSize={13.5}>Tenants using {trade?.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Push a new version to all or specific tenants currently adopting this trade — not future tenants (that's the Provider default above).
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id="save-target-tenants-label">Select tenant(s)</InputLabel>
              <Select<string[]>
                labelId="save-target-tenants-label"
                label="Select tenant(s)"
                multiple
                value={tenantIds}
                onChange={handleTenantChange}
                renderValue={(sel) => {
                  if (!sel.length) return '';
                  if (allSelected) return `All tenants (${mappedTenantIds.length})`;
                  return sel.map(id => mappedTenants.find(t => t.id === id)?.name ?? id).join(', ');
                }}
              >
                <MenuItem value={ALL_TENANTS}>
                  <Checkbox checked={allSelected} indeterminate={tenantIds.length > 0 && !allSelected} size="small" />
                  <ListItemText primary={`All tenants (${mappedTenantIds.length})`} />
                </MenuItem>
                {mappedTenants.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    <Checkbox checked={tenantIds.includes(t.id)} size="small" />
                    <ListItemText primary={t.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <TextField label="Version note" fullWidth size="small" value={note} onChange={e => setNote(e.target.value)} sx={{ mt: 1 }} />
        {!anyTarget && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>Select at least one destination.</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!anyTarget}
          onClick={() => onConfirm({ toProvider, toCurrentTenant, tenantIds: canPushAllTenants ? tenantIds : [], note: note || 'Updated template' })}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
